import { ChevronRight } from 'lucide-react';
import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';

import { CheckboxGlyph } from '@/ux-common/components/checkbox/checkbox';
import { cn } from '@/ux-common/lib/utils';

/**
 * DropdownMenu — a command menu on @radix-ui/react-dropdown-menu (WAI-ARIA menu
 * button: roving focus, arrow keys, typeahead, Escape, submenus).
 *
 * All visual styling is applied via CSS classes. The wrappers add ref forwarding,
 * `data-component`, `className` merging and the Figma chrome: a white popout
 * panel with 40px rows. Checkbox and radio items draw the registry's own
 * `.checkbox` / `.radio` (inverse variant — the panel is a light surface), and
 * the row's state drives them.
 *
 * Exports keep the ckds DropdownMenu's names, so migrating is an import-path
 * change plus the class-based API below.
 *
 * Modifiers are classes, not props:
 * - `dropdown-menu-item-inset` indents a row by one control, for the children
 *   of a "select all" checkbox.
 *
 * On its own it is not a form field: a menu's items are commands with
 * `role="menuitem"`, and it has no label, value or error state. For one value use
 * Select; to open a menu from a labelled field (several values from checkbox rows),
 * use DropdownInput, which wraps this in an Input group.
 *
 * @example
 * ```tsx
 * <DropdownMenu>
 *   <DropdownMenuTrigger asChild>
 *     <DropdownButton className="btn-tier3">Options</DropdownButton>
 *   </DropdownMenuTrigger>
 *   <DropdownMenuContent>
 *     <DropdownMenuLabel>View</DropdownMenuLabel>
 *     <DropdownMenuCheckboxItem checked={grid} onCheckedChange={setGrid}>
 *       Grid lines
 *     </DropdownMenuCheckboxItem>
 *     <DropdownMenuSeparator />
 *     <DropdownMenuItem onSelect={exportCsv}>Export CSV</DropdownMenuItem>
 *   </DropdownMenuContent>
 * </DropdownMenu>
 * ```
 */

export const DropdownMenu = DropdownMenuPrimitive.Root;

export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

export const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

export const DropdownMenuSub = DropdownMenuPrimitive.Sub;

export type DropdownMenuTriggerProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Trigger
>;

/* ================================================================
   Groups
   ================================================================ */

export type DropdownMenuGroupProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Group
>;

/**
 * DropdownMenuGroup — a `<div role="group">` of rows. `.dropdown-menu-group`
 * keeps the panel's row rhythm inside it; the class, not the role, is what the
 * CSS targets, so a `role="group"` in custom content is left alone.
 */
export const DropdownMenuGroup = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Group>,
  DropdownMenuGroupProps
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Group
    ref={ref}
    data-component="DropdownMenuGroup"
    className={cn('dropdown-menu-group', className)}
    {...props}
  />
));

DropdownMenuGroup.displayName = 'DropdownMenuGroup';

export type DropdownMenuRadioGroupProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.RadioGroup
>;

/**
 * DropdownMenuRadioGroup — the group that holds one choice among its
 * `DropdownMenuRadioItem`s (`value` / `onValueChange`). Laid out like
 * `DropdownMenuGroup`.
 */
export const DropdownMenuRadioGroup = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioGroup>,
  DropdownMenuRadioGroupProps
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioGroup
    ref={ref}
    data-component="DropdownMenuRadioGroup"
    className={cn('dropdown-menu-group', className)}
    {...props}
  />
));

DropdownMenuRadioGroup.displayName = 'DropdownMenuRadioGroup';

/* ================================================================
   Content
   ================================================================ */

export type DropdownMenuContentProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Content
>;

/**
 * DropdownMenuContent — the popout panel. Always rendered in a portal, so a
 * scrolling or `overflow: hidden` ancestor cannot clip it. Opens 2px below the
 * trigger, aligned to its start edge, as drawn.
 */
export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(({ className, sideOffset = 2, align = 'start', ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      data-component="DropdownMenuContent"
      sideOffset={sideOffset}
      align={align}
      className={cn('dropdown-menu', className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));

DropdownMenuContent.displayName = 'DropdownMenuContent';

export type DropdownMenuSubContentProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.SubContent
>;

/** DropdownMenuSubContent — a submenu's panel; the same chrome as the menu. */
export const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  DropdownMenuSubContentProps
>(({ className, sideOffset = 0, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      data-component="DropdownMenuSubContent"
      sideOffset={sideOffset}
      className={cn('dropdown-menu', className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));

DropdownMenuSubContent.displayName = 'DropdownMenuSubContent';

/* ================================================================
   Items
   ================================================================ */

/** The prop every item takes on top of its Radix props. */
type RefusableItemProps = {
  /**
   * Refuses the item and says why. The row stays in the arrow-key order (WAI-ARIA
   * APG: a disabled menu item is focusable) but cannot be chosen, and the reason
   * is announced as its description — not shown: nothing is added to the row's
   * look. For a visible reason, wrap the item in a Tooltip
   * (`<TooltipTrigger asChild aria-describedby={undefined}>` — without that, the
   * open tooltip's description repeats the reason). Use it when the user should learn why an action
   * is unavailable — a view-only session, a missing permission. Plain `disabled`
   * keeps Radix's behaviour: the row is skipped — and a click on it calls nothing,
   * not even your `onClick`. Wins over `disabled` when both are set.
   */
  disabledReason?: React.ReactNode;
};

type RefusalInput = {
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  'aria-describedby'?: string;
};

/** Stands in for the consumer's onClick on a row that cannot be chosen. */
const blockClick = (event: React.MouseEvent<HTMLDivElement>) => event.preventDefault();

/**
 * The props that refuse an item, and the reason to render inside it — or nothing
 * when `disabledReason` is empty.
 *
 * Radix's `disabled` takes the row out of roving focus, so a refused row never
 * passes it. It blocks the row at `onClick` instead: Radix runs its select (and a
 * checkbox's `onCheckedChange`, a radio's `onValueChange`, the menu closing) only
 * when the click is not default-prevented, and Enter / Space reach it as a click.
 * Preventing `onSelect` would not be enough — the checkbox and radio items change
 * their value even when it is prevented. `aria-disabled` and `data-disabled` are
 * set by hand, so the row reads and styles as disabled.
 *
 * The reason is visually hidden (`sr-only`), and `aria-hidden` so it is not read
 * again as part of the row's name; `aria-describedby` still reaches it.
 *
 * A plain `disabled` row is blocked at `onClick` too. A menu row is a div, so the
 * browser suppresses nothing, and Radix gates only its own select — the
 * consumer's onClick would still run, and an `asChild` link would still navigate.
 * No `pointer-events: none` either: it would kill hover for the whole row.
 */
function useRefusal(disabledReason: React.ReactNode, props: RefusalInput) {
  const reasonId = React.useId();
  const refused = disabledReason != null && disabledReason !== false && disabledReason !== '';
  if (!refused) {
    return { itemProps: props.disabled ? { ...props, onClick: blockClick } : props, reason: null };
  }

  return {
    itemProps: {
      disabled: false,
      'aria-disabled': true,
      'data-disabled': '',
      'aria-describedby': [reasonId, props['aria-describedby']].filter(Boolean).join(' '),
      // The consumer's onClick is not called either: a refused row does nothing.
      onClick: blockClick,
    },
    reason: (
      <span id={reasonId} aria-hidden="true" className="sr-only">
        {disabledReason}
      </span>
    ),
  };
}

/**
 * A plain item's children with its reason after them. With no reason the children
 * pass through untouched — an `asChild` item must keep exactly one child for Radix's
 * Slot to land on. With `asChild` and a reason, the reason goes inside the slotted
 * element (an `<a>`, a router `Link`), since it can have no sibling.
 */
function withReason(
  children: React.ReactNode,
  reason: React.ReactNode,
  asChild: boolean | undefined
): React.ReactNode {
  if (!reason) return children;
  if (asChild && React.isValidElement<{ children?: React.ReactNode }>(children)) {
    return React.cloneElement(children, undefined, children.props.children, reason);
  }
  return (
    <>
      {children}
      {reason}
    </>
  );
}

export type DropdownMenuItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Item
> &
  RefusableItemProps;

/**
 * DropdownMenuItem — one command. Pass a leading 16px glyph as the first child
 * and the text after it; anything after the text sits in the row's trailing
 * space. `disabledReason` refuses it while keeping it reachable (see
 * `RefusableItemProps`).
 */
export const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(
  (
    {
      children,
      className,
      disabled,
      disabledReason,
      onClick,
      'aria-describedby': describedBy,
      ...props
    },
    ref
  ) => {
    const { itemProps, reason } = useRefusal(disabledReason, {
      disabled,
      onClick,
      'aria-describedby': describedBy,
    });
    return (
      <DropdownMenuPrimitive.Item
        ref={ref}
        data-component="DropdownMenuItem"
        className={cn('dropdown-menu-item', className)}
        {...props}
        {...itemProps}
      >
        {withReason(children, reason, props.asChild)}
      </DropdownMenuPrimitive.Item>
    );
  }
);

DropdownMenuItem.displayName = 'DropdownMenuItem';

export type DropdownMenuSubTriggerProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.SubTrigger
>;

/** DropdownMenuSubTrigger — a row that opens a submenu; renders the trailing chevron. */
export const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  DropdownMenuSubTriggerProps
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    data-component="DropdownMenuSubTrigger"
    className={cn('dropdown-menu-item dropdown-menu-sub-trigger', className)}
    {...props}
  >
    {children}
    <ChevronRight aria-hidden="true" className="dropdown-menu-chevron" />
  </DropdownMenuPrimitive.SubTrigger>
));

DropdownMenuSubTrigger.displayName = 'DropdownMenuSubTrigger';

export type DropdownMenuCheckboxItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.CheckboxItem
> &
  RefusableItemProps;

/**
 * DropdownMenuCheckboxItem — a toggle row. Draws the registry `.checkbox` in its
 * inverse variant; the row's `data-state` and `data-disabled` drive its colours.
 * `checked="indeterminate"` renders the minus, for a "select all" row whose
 * children are partly checked.
 *
 * Toggling keeps the menu open, as the ckds DropdownMenu does, so several rows
 * can be toggled in one visit. The consumer's `onSelect` still runs; close the
 * menu through its controlled `open` state if a row should dismiss it.
 * `disabledReason` refuses it while keeping it reachable.
 */
export const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  DropdownMenuCheckboxItemProps
>(
  (
    {
      className,
      children,
      checked,
      disabled,
      disabledReason,
      onClick,
      onSelect,
      'aria-describedby': describedBy,
      ...props
    },
    ref
  ) => {
    const { itemProps, reason } = useRefusal(disabledReason, {
      disabled,
      onClick,
      'aria-describedby': describedBy,
    });
    return (
      <DropdownMenuPrimitive.CheckboxItem
        ref={ref}
        data-component="DropdownMenuCheckboxItem"
        className={cn('dropdown-menu-item dropdown-menu-checkbox-item', className)}
        checked={checked}
        onSelect={(event) => {
          // Radix closes the menu after a selection unless it is prevented. Prevent
          // first, so a consumer handler cannot undo the keep-open by omission.
          event.preventDefault();
          onSelect?.(event);
        }}
        {...props}
        {...itemProps}
      >
        {/* A <button> cannot sit inside a menu row, so the row draws the checkbox's
            class API around Checkbox's own glyph rather than rendering Checkbox. */}
        <span aria-hidden="true" className="checkbox checkbox-inverse">
          <DropdownMenuPrimitive.ItemIndicator className="checkbox-indicator">
            <CheckboxGlyph checked={checked} />
          </DropdownMenuPrimitive.ItemIndicator>
        </span>
        {children}
        {reason}
      </DropdownMenuPrimitive.CheckboxItem>
    );
  }
);

DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem';

export type DropdownMenuRadioItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.RadioItem
> &
  RefusableItemProps;

/**
 * DropdownMenuRadioItem — one option of a `DropdownMenuRadioGroup`. Draws the
 * registry `.radio` in its inverse variant; the row's state drives it.
 * `disabledReason` refuses it while keeping it reachable.
 *
 * SelectItem copies this markup to get the same look: change both together (a
 * Select test compares them).
 */
export const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  DropdownMenuRadioItemProps
>(
  (
    {
      className,
      children,
      disabled,
      disabledReason,
      onClick,
      'aria-describedby': describedBy,
      ...props
    },
    ref
  ) => {
    const { itemProps, reason } = useRefusal(disabledReason, {
      disabled,
      onClick,
      'aria-describedby': describedBy,
    });
    return (
      <DropdownMenuPrimitive.RadioItem
        ref={ref}
        data-component="DropdownMenuRadioItem"
        className={cn('dropdown-menu-item dropdown-menu-radio-item', className)}
        {...props}
        {...itemProps}
      >
        <span aria-hidden="true" className="radio radio-inverse">
          <DropdownMenuPrimitive.ItemIndicator className="radio-indicator" />
        </span>
        {children}
        {reason}
      </DropdownMenuPrimitive.RadioItem>
    );
  }
);

DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem';

/* ================================================================
   Label and separator
   ================================================================ */

export type DropdownMenuLabelProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Label
>;

/** DropdownMenuLabel — a non-interactive group heading, set in caps. */
export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  DropdownMenuLabelProps
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    data-component="DropdownMenuLabel"
    className={cn('dropdown-menu-label', className)}
    {...props}
  />
));

DropdownMenuLabel.displayName = 'DropdownMenuLabel';

export type DropdownMenuSeparatorProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Separator
>;

/** DropdownMenuSeparator — a 1px rule between groups. */
export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  DropdownMenuSeparatorProps
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    data-component="DropdownMenuSeparator"
    className={cn('dropdown-menu-separator', className)}
    {...props}
  />
));

DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';
